import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import DropdownArrow from '../__partials/dropdown-arrow';
import NoMatchingResults from '../__partials/no-matching-results';
import Option from '../__partials/option';
import OptionGroup from '../__partials/option-group';
import Dismiss from '../../__partials/dismiss';
import Label from '../../forms/__partials/label';
import HelpText from '../../forms/__partials/help-text';
import idGenerator from '../../__utils/idGenerator';
import Pill from '../__partials/pill';
import classNames from 'classnames';
import {
  isItemSelected,
  isItemHighlighted,
  generateIdGenerator,
  getMaxHeightStyles,
  itemToString,
  filterOnHead,
  filterOnFullString,
  KeyCodes,
  shouldRenderOptionGroups,
} from '../../__utils/selectHelpers';
import { OptionsPropType } from '../../__utils/selectPropTypes';

import '../style.scss';

export default class LookupMultiSelect extends Component {
  constructor(props) {
    super(props);

    this.id = this.props.id || idGenerator();
    this.qaId = this.props.qaId || this.id;

    this.idGenerator = generateIdGenerator(this.id);
    this.qaIdGenerator = generateIdGenerator(this.qaId);

    let selItems = [...(this.props.defaultValues || [])];
    if (this.props.defaultValues) {
      selItems = selItems.map(selItem => {
        if (['number', 'string'].includes(typeof selItem)) {
          return this.props.options.find(option => option.value === selItem);
        }
        return selItem;
      });
    }

    this.state = {
      selectedItems: selItems,
    };
  }

  /**
   * Its possible to not provide a value prop to this component.
   * This is a quick way to check if there was a value prop provided
   */
  shouldUseValuesProp() {
    return this.props.hasOwnProperty('values');
  }

  getValuesAsOptions = () => {
    let values = [...(this.props.values || [])];
    values = values.map(value => {
      if (['number', 'string'].includes(typeof value)) {
        return this.props.options.find(option => option.value === value);
      }
      return value;
    });
    return values;
  };

  onChange = selectedItem => {
    if (this.shouldUseValuesProp()) {
      const values = this.getValuesAsOptions();
      if (values.find(item => item.value === selectedItem.value)) {
        this.props.onRemoveItem(selectedItem, values.filter(v => v.value !== selectedItem.value));
      } else {
        this.props.onAddItem(selectedItem, [...values, selectedItem]);
      }
    } else {
      if (this.state.selectedItems.find(item => item.value === selectedItem.value)) {
        const nextSelectedItems = this.state.selectedItems.filter(s => s !== selectedItem);
        this.setState({ selectedItems: nextSelectedItems }, () => {
          this.props.onRemoveItem && this.props.onRemoveItem(selectedItem, this.state.selectedItems);
        });
      } else {
        const nextSelectedItems = [...this.state.selectedItems, selectedItem];
        this.setState({ selectedItems: nextSelectedItems }, () => {
          this.props.onAddItem && this.props.onAddItem(selectedItem, this.state.selectedItems);
        });
      }
    }
  };

  setInputRef = ref => {
    this.props.setInputRef && this.props.setInputRef(ref);
    this.inputRef = ref;
  };

  focusInput = () => {
    setTimeout(() => {
      this.inputRef && this.inputRef.focus();
    }, 0);
  };

  removeLastItem = () => {
    if (this.shouldUseValuesProp()) {
      const values = this.getValuesAsOptions();
      if (!values.length) return;
      const nextValues = [...values];
      const removedValue = nextValues.pop();

      this.props.onRemoveItem(removedValue, nextValues);
    } else {
      const { selectedItems } = this.state;
      if (!selectedItems.length) return;
      const nextSelectedItems = [...selectedItems];
      const removedItem = nextSelectedItems.pop();
      this.setState({ selectedItems: nextSelectedItems }, () => {
        this.props.onRemoveItem && this.props.onRemoveItem(removedItem, this.state.selectedItems);
      });
    }
  };

  removeAllItems = () => {
    if (this.shouldUseValuesProp()) {
      const values = this.getValuesAsOptions();
      this.props.onRemoveAllItems && this.props.onRemoveAllItems(values);
    } else {
      const { selectedItems } = this.state;
      if (!selectedItems.length) return;
      this.setState({ selectedItems: [] }, () => {
        this.props.onRemoveAllItems && this.props.onRemoveAllItems(selectedItems);
      });
    }
  };

  onDownshiftStateChange = newDownShiftState => {
    if (newDownShiftState.isOpen && this.props.hasOwnProperty('onMenuOpen')) {
      this.props.onMenuOpen();
    }
  };

  downshiftStateReducer = (state, changes) => {
    // this prevents the menu from being closed when the user
    // selects an item with a keyboard or mouse
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          isOpen: !state.isOpen,
          highlightedIndex: state.highlightedIndex,
          inputValue: '',
        };
      default:
        return changes;
    }
  };

  getFilterFunction() {
    return this.props.matchHead ? filterOnHead : filterOnFullString;
  }

  renderOptionList(downshiftProps, options, baseIndex = 0) {
    return options.map((option, itemIndex) => {
      const index = baseIndex + itemIndex;
      const idDescriptor = `${option.value}-option`;

      const { getItemProps, highlightedIndex, inputValue } = downshiftProps;
      const selectedItems = this.shouldUseValuesProp() ? this.getValuesAsOptions() : this.state.selectedItems;
      const selectedItem = selectedItems.find(s => s.value === option.value);

      return (
        <Option
          {...getItemProps({ item: option })}
          optionData={option}
          isSelected={isItemSelected(selectedItem, option)}
          isHighlighted={isItemHighlighted(highlightedIndex, index)}
          matchData={inputValue}
          matchHead={this.props.matchHead}
          key={option.value}
          qaId={this.qaIdGenerator(idDescriptor)}
        >
          {this.props.customOption}
        </Option>
      );
    });
  }

  renderOptionGroups(downshiftProps, optionGroups) {
    return Object.keys(optionGroups).reduce(
      (result, groupKey) => {
        const group = optionGroups[groupKey];

        const filteredOptions = this.getFilterFunction()(group.options, downshiftProps.inputValue);
        if (filteredOptions.length === 0) return result;

        const idDescriptor = `${groupKey}-options-group`;

        const optionGroup = (
          <OptionGroup
            key={groupKey}
            title={group.label}
            id={this.idGenerator(idDescriptor)}
            qaId={this.qaIdGenerator(idDescriptor)}
          >
            {this.renderOptionList(downshiftProps, filteredOptions, result.itemIndex)}
          </OptionGroup>
        );

        return {
          groups: [...result.groups, optionGroup],
          itemIndex: result.itemIndex + filteredOptions.length,
        };
      },
      { groups: [], itemIndex: 0 }
    ).groups;
  }

  renderOptions(downshiftProps, options) {
    const optionsList = shouldRenderOptionGroups(options)
      ? this.renderOptionGroups(downshiftProps, options)
      : this.renderOptionList(downshiftProps, this.getFilterFunction()(options, downshiftProps.inputValue));

    return optionsList.length > 0 ? optionsList : <NoMatchingResults text={this.props.noMatchingResultsText} />;
  }

  render() {
    const {
      options,
      label,
      hideLabel,
      helpText,
      hasError,
      required,
      disabled,
      maxHeight,
      maxInputHeight,
      placeholder,
      values,
      name,
    } = this.props;
    const hasSelectedItems = this.shouldUseValuesProp()
      ? !!(this.props.values && this.props.values.length)
      : !!this.state.selectedItems.length;

    const formItemClasses = classNames('uni-form__item', {
      'uni-form__item--is-error': hasError,
      'uni-form__item--is-disabled': disabled,
    });

    const valueOptions = [...(values || [])];
    if (this.shouldUseValuesProp()) {
      valueOptions.map(valueOption => {
        if (['number', 'string'].includes(typeof valueOption)) {
          return this.props.options.find(option => option.value === valueOption);
        }
        return valueOption;
      });
    }

    return (
      <Downshift
        onChange={this.onChange}
        itemToString={itemToString}
        id={this.id}
        selectedItem={null}
        onStateChange={this.onDownshiftStateChange}
        stateReducer={this.downshiftStateReducer}
      >
        {downshiftProps => {
          const { isOpen, highlightedIndex, inputValue } = downshiftProps;

          const defaultInputProps = downshiftProps.getInputProps({
            id: this.idGenerator('input'),
            placeholder: !hasSelectedItems ? placeholder : '',
            onBlur: e => (e.preventDownshiftDefault = true),
          });

          const onInputKeyDown = e => {
            const nothingIsHighlighted = highlightedIndex === null;
            const inputIsEmpty = !inputValue;

            if (inputIsEmpty && hasSelectedItems && e.keyCode === KeyCodes.BACKSPACE) {
              this.removeLastItem();
              downshiftProps.openMenu();
              return;
            }

            // If the key is something other than enter or space, we want to fail
            // this evaluation immediately
            if (e.keyCode === KeyCodes.SPACE) {
              if (!isOpen) {
                e.preventDefault();
                downshiftProps.toggleMenu();
                return;
              }
            } else if (e.keyCode === KeyCodes.ENTER) {
              if (!isOpen || nothingIsHighlighted) {
                e.preventDefault();
                return;
              }
            }

            defaultInputProps.onKeyDown(e);
          };

          const inputProps = {
            ...defaultInputProps,
            disabled: disabled,
            onKeyDown: onInputKeyDown,
          };

          const selectClasses = classNames('uni-select', {
            'uni-select--is-open': isOpen,
          });

          return (
            <div className={formItemClasses}>
              {label && (
                <Label label={label} required={required} name={this.idGenerator('input')} hideLabel={hideLabel} />
              )}
              <div
                className={selectClasses}
                id={this.id}
                onClick={() => {
                  downshiftProps.toggleMenu();
                  this.focusInput();
                }}
              >
                <div
                  className="uni-select__input-container"
                  style={{ 'maxHeight': maxInputHeight, 'overflow': 'auto' }}
                  id={this.id}
                  data-qa-id={this.qaId}
                  ref={ref => (this.containerRef = ref)}
                >
                  {(this.shouldUseValuesProp() ? this.getValuesAsOptions() : this.state.selectedItems).map(s => (
                    <Pill
                      key={s.value}
                      text={s.label}
                      onDismiss={e => {
                        this.onChange(s);
                        e.stopPropagation();
                      }}
                    />
                  ))}
                  <input
                    {...inputProps}
                    className="uni-select__input"
                    data-qa-id={this.qaIdGenerator('input')}
                    ref={this.setInputRef}
                  />
                  <span className="uni-select__input-controls">
                    {hasSelectedItems && (
                      <Dismiss
                        size="small"
                        className="uni-select__input-dismiss"
                        onClick={() => {
                          this.removeAllItems();
                          downshiftProps.clearSelection();
                          downshiftProps.openMenu();
                        }}
                      />
                    )}

                    <DropdownArrow />
                  </span>
                </div>

                {!this.shouldUseValuesProp() && (
                  <input
                    type="hidden"
                    value={this.state.selectedItems.map(s => s.value).join()}
                    name={name}
                    id={this.idGenerator('hidden-input')}
                    data-qa-id={this.qaIdGenerator('hidden-input')}
                  />
                )}

                <ul
                  className="uni-select__items"
                  style={getMaxHeightStyles(maxHeight, isOpen)}
                  id={this.idGenerator('options-list')}
                >
                  {this.renderOptions(downshiftProps, options)}
                </ul>
              </div>

              {helpText && <HelpText name={this.idGenerator('input')} text={helpText} hasError={hasError} />}
            </div>
          );
        }}
      </Downshift>
    );
  }
}

LookupMultiSelect.propTypes = {
  options: OptionsPropType,
  customOption: PropTypes.any,
  id: PropTypes.string,
  qaId: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helpText: PropTypes.string,
  hasError: PropTypes.bool,
  matchHead: PropTypes.bool,
  maxHeight: PropTypes.string,
  maxInputHeight: PropTypes.string,
  noMatchingResultsText: PropTypes.any,
  onAddItem: PropTypes.func,
  onRemoveItem: PropTypes.func,
  onRemoveAllItems: PropTypes.func,
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
  defaultValues: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
  placeholder: PropTypes.string,
  onMenuOpen: PropTypes.func,
  setInputRef: PropTypes.func,
};

LookupMultiSelect.defaultProps = {
  matchHead: false,
  noMatchingResultsText: 'No matching results',
};



// WEBPACK FOOTER //
// ./src/selects/lookup-multi-select/index.jsx